/**
 * Service for Skills and People Occupations API
 * Base URL: https://apicabaza.creago.be/rest/cabaza/skills
 */


const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/skills';
const OCCUPATIONS_API_URL = 'https://apicabaza.creago.be/rest/cabaza/occupations';

/**
 * Get skills for an occupation
 * @param {number} occupationId - Occupation ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of skills
 */
export const getSkillsForOccupation = async (occupationId, lang = 'fr') => {
    try {
        const params = new URLSearchParams({ lang });

        const response = await fetch(`${API_BASE_URL}/occupation/${occupationId}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                escoUri: item.escouri ?? item.ESCOURI,
                skillType: item.skilltype ?? item.SKILLTYPE,
                name_fr: item.name_fr ?? item.NAME_FR ?? '',
                name_en: item.name_en ?? item.NAME_EN ?? '',
                name_nl: item.name_nl ?? item.NAME_NL ?? '',
                name_de: item.name_de ?? item.NAME_DE ?? '',
                description_fr: item.description_fr ?? item.DESCRIPTION_FR ?? '',
                description_en: item.description_en ?? item.DESCRIPTION_EN ?? '',
                isEssential: item.isessential ?? item.ISESSENTIAL ?? false
            }));
        }

        return [];
    } catch (error) {
        console.error('Error getting skills for occupation:', error);
        return [];
    }
};

/**
 * Fetch and save ESCO skills for an occupation
 * @param {number} occupationId - Occupation ID
 * @returns {Promise<Object>} Result with imported skills count
 */
export const fetchEscoSkillsForOccupation = async (occupationId) => {
    try {
        const response = await fetch(`${OCCUPATIONS_API_URL}/${occupationId}/esco-skills`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];
        const essentialCount = result.essentialCount ?? result.ESSENTIALCOUNT ?? 0;
        const optionalCount = result.optionalCount ?? result.OPTIONALCOUNT ?? 0;
        const message = result.message ?? result.MESSAGE ?? '';

        return {
            success,
            data,
            essentialCount,
            optionalCount,
            message
        };
    } catch (error) {
        console.error('Error fetching ESCO skills:', error);
        return { success: false, data: [], essentialCount: 0, optionalCount: 0, message: error.message };
    }
};

/**
 * Get skills for a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Optional occupation filter
 * @param {string} lang - Language code
 * @returns {Promise<Object>} Object with skills array and skillIds
 */
export const getPeopleSkills = async (peopleUuid, occupationId = null, lang = 'fr') => {
    try {
        const params = new URLSearchParams({ lang });
        if (occupationId) {
            params.append('occupationId', occupationId);
        }

        const response = await fetch(`${API_BASE_URL}/people/${peopleUuid}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];
        const skillIds = result.skillIds ?? result.SKILLIDS ?? [];

        if (success) {
            return {
                skills: data.map(item => ({
                    id: item.id ?? item.ID,
                    escoUri: item.escouri ?? item.ESCOURI,
                    skillType: item.skilltype ?? item.SKILLTYPE,
                    name_fr: item.name_fr ?? item.NAME_FR ?? '',
                    name_en: item.name_en ?? item.NAME_EN ?? '',
                    occupationId: item.occupationid ?? item.OCCUPATIONID
                })),
                skillIds: skillIds.map(id => parseInt(id, 10))
            };
        }

        return { skills: [], skillIds: [] };
    } catch (error) {
        console.error('Error getting people skills:', error);
        return { skills: [], skillIds: [] };
    }
};

/**
 * Toggle a skill for a person (add or remove)
 * @param {string} peopleUuid - Person UUID
 * @param {number} skillId - Skill ID
 * @param {string} action - 'add' or 'remove'
 * @param {number} occupationId - Optional occupation ID
 * @returns {Promise<Object>} Result
 */
export const togglePeopleSkill = async (peopleUuid, skillId, action = 'add', occupationId = null) => {
    try {
        const body = { skillId, action };
        if (occupationId) {
            body.occupationId = occupationId;
        }

        const response = await fetch(`${API_BASE_URL}/people/${peopleUuid}/toggle`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error toggling people skill:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get occupations for a person
 * @param {string} peopleUuid - Person UUID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of occupations
 */
export const getPeopleOccupations = async (peopleUuid, lang = 'fr') => {
    try {
        const params = new URLSearchParams({ lang });

        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                escoUri: item.escouri ?? item.ESCOURI,
                name_fr: item.name_fr ?? item.NAME_FR ?? '',
                name_en: item.name_en ?? item.NAME_EN ?? '',
                name_nl: item.name_nl ?? item.NAME_NL ?? '',
                name_de: item.name_de ?? item.NAME_DE ?? '',
                isPrimary: item.isprimary ?? item.ISPRIMARY ?? false,
                createdAt: item.createdat ?? item.CREATEDAT
            }));
        }

        return [];
    } catch (error) {
        console.error('Error getting people occupations:', error);
        return [];
    }
};

/**
 * Add an occupation to a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Occupation ID
 * @param {boolean} isPrimary - Set as primary occupation
 * @returns {Promise<Object>} Result
 */
export const addPeopleOccupation = async (peopleUuid, occupationId, isPrimary = false) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ occupationId, isPrimary })
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding people occupation:', error);
        return { success: false, id: 0, message: error.message };
    }
};

/**
 * Remove an occupation from a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Occupation ID
 * @returns {Promise<Object>} Result
 */
export const removePeopleOccupation = async (peopleUuid, occupationId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}/${occupationId}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error removing people occupation:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Set primary occupation for a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Occupation ID
 * @returns {Promise<Object>} Result
 */
export const setPrimaryOccupation = async (peopleUuid, occupationId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}/primary/${occupationId}`, {
            method: 'PUT',
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error setting primary occupation:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Search skills in ESCO database
 * @param {string} query - Search query
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of skills
 */
export const searchSkillsEsco = async (query, lang = 'fr') => {
    try {
        if (!query || query.length < 2) return [];

        const params = new URLSearchParams({ q: query, lang });

        const response = await fetch(`${API_BASE_URL}/search?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                escoUri: item.escouri ?? item.ESCOURI,
                skillType: item.skilltype ?? item.SKILLTYPE,
                name_fr: item.name_fr ?? item.NAME_FR ?? '',
                name_en: item.name_en ?? item.NAME_EN ?? '',
                name_nl: item.name_nl ?? item.NAME_NL ?? '',
                name_de: item.name_de ?? item.NAME_DE ?? ''
            }));
        }

        return [];
    } catch (error) {
        console.error('Error searching skills:', error);
        return [];
    }
};

/**
 * Fill missing translation for a skill
 * @param {number} skillId - Skill ID
 * @param {string} lang - Language code
 * @returns {Promise<Object>} Result with translated name
 */
export const fillSkillTranslation = async (skillId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE_URL}/${skillId}/fill-translation`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lang })
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            name: result.name ?? result.NAME ?? '',
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error filling skill translation:', error);
        return { success: false, name: '', message: error.message };
    }
};

// =============================================
// Extended Skills Functions (with proficiency)
// =============================================

/**
 * Proficiency levels mapping
 */
export const PROFICIENCY_LEVELS = {
    1: { key: 'beginner', label_fr: 'Débutant', label_en: 'Beginner', label_nl: 'Beginner', label_de: 'Anfänger' },
    2: { key: 'intermediate', label_fr: 'Intermédiaire', label_en: 'Intermediate', label_nl: 'Gemiddeld', label_de: 'Mittel' },
    3: { key: 'advanced', label_fr: 'Avancé', label_en: 'Advanced', label_nl: 'Gevorderd', label_de: 'Fortgeschritten' },
    4: { key: 'expert', label_fr: 'Expert', label_en: 'Expert', label_nl: 'Expert', label_de: 'Experte' }
};

/**
 * Priority levels for project skills
 */
export const PRIORITY_LEVELS = {
    1: { key: 'high', label_fr: 'Haute', label_en: 'High', label_nl: 'Hoog', label_de: 'Hoch' },
    2: { key: 'medium', label_fr: 'Moyenne', label_en: 'Medium', label_nl: 'Gemiddeld', label_de: 'Mittel' },
    3: { key: 'low', label_fr: 'Basse', label_en: 'Low', label_nl: 'Laag', label_de: 'Niedrig' }
};

/**
 * Get skills with proficiency levels for a person
 * @param {number} peopleId - Person ID
 * @param {boolean} onlyHighlighted - Only return highlighted skills
 * @returns {Promise<Array>} Array of skills with proficiency
 */
export const getPeopleSkillsExtended = async (peopleId, onlyHighlighted = false) => {
    try {
        const params = new URLSearchParams();
        if (onlyHighlighted) params.append('onlyHighlighted', 'true');

        const url = `${API_BASE_URL}/people-extended/${peopleId}${params.toString() ? '?' + params : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                peopleId: item.people_id ?? item.PEOPLE_ID,
                skillUri: item.skill_uri ?? item.SKILL_URI,
                skillLabel: item.skill_label ?? item.SKILL_LABEL,
                skillDescription: item.skill_description ?? item.SKILL_DESCRIPTION ?? '',
                skillType: item.skill_type ?? item.SKILL_TYPE ?? '',
                proficiencyLevel: item.proficiency_level ?? item.PROFICIENCY_LEVEL ?? 2,
                yearsExperience: item.years_experience ?? item.YEARS_EXPERIENCE ?? 0,
                occupationGroupSkillId: item.occupation_group_skill_id ?? item.OCCUPATION_GROUP_SKILL_ID,
                isPublic: item.is_public ?? item.IS_PUBLIC ?? 1,
                isHighlighted: item.is_highlighted ?? item.IS_HIGHLIGHTED ?? 0,
                sortOrder: item.sort_order ?? item.SORT_ORDER ?? 0,
                endorsementCount: item.endorsement_count ?? item.ENDORSEMENT_COUNT ?? 0,
                createdAt: item.created_at ?? item.CREATED_AT,
                updatedAt: item.updated_at ?? item.UPDATED_AT
            }));
        }

        return [];
    } catch (error) {
        console.error('Error getting extended people skills:', error);
        return [];
    }
};

/**
 * Add a skill with proficiency to a person
 * @param {number} peopleId - Person ID
 * @param {Object} skillData - Skill data
 * @returns {Promise<Object>} Result
 */
export const addPeopleSkillExtended = async (peopleId, skillData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-extended/${peopleId}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(skillData)
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding extended people skill:', error);
        return { success: false, id: 0, message: error.message };
    }
};

/**
 * Update a skill proficiency
 * @param {number} skillId - Skill ID (peoples_skills.id)
 * @param {Object} updateData - Update data (proficiencyLevel, yearsExperience, isPublic, isHighlighted, sortOrder)
 * @returns {Promise<Object>} Result
 */
export const updatePeopleSkillExtended = async (skillId, updateData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-extended/${skillId}`, {
            method: 'PUT',
            credentials: 'include',
            body: JSON.stringify(updateData)
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error updating extended people skill:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a skill from a person (soft delete)
 * @param {number} skillId - Skill ID
 * @returns {Promise<Object>} Result
 */
export const deletePeopleSkillExtended = async (skillId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-extended/${skillId}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error deleting extended people skill:', error);
        return { success: false, message: error.message };
    }
};

// =============================================
// Project Skills Functions
// =============================================

/**
 * Get required skills for a project
 * @param {number} projectId - Project ID
 * @returns {Promise<Array>} Array of required skills
 */
export const getProjectSkills = async (projectId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/project/${projectId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                projectId: item.project_id ?? item.PROJECT_ID,
                skillUri: item.skill_uri ?? item.SKILL_URI,
                skillLabel: item.skill_label ?? item.SKILL_LABEL,
                skillDescription: item.skill_description ?? item.SKILL_DESCRIPTION ?? '',
                skillType: item.skill_type ?? item.SKILL_TYPE ?? '',
                requiredLevel: item.required_level ?? item.REQUIRED_LEVEL ?? 2,
                isRequired: item.is_required ?? item.IS_REQUIRED ?? 1,
                priority: item.priority ?? item.PRIORITY ?? 2,
                occupationGroupSkillId: item.occupation_group_skill_id ?? item.OCCUPATION_GROUP_SKILL_ID,
                sortOrder: item.sort_order ?? item.SORT_ORDER ?? 0,
                addedAt: item.added_at ?? item.ADDED_AT,
                addedBy: item.added_by ?? item.ADDED_BY
            }));
        }

        return [];
    } catch (error) {
        console.error('Error getting project skills:', error);
        return [];
    }
};

/**
 * Add a required skill to a project
 * @param {number} projectId - Project ID
 * @param {Object} skillData - Skill data
 * @returns {Promise<Object>} Result
 */
export const addProjectSkill = async (projectId, skillData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/project/${projectId}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(skillData)
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding project skill:', error);
        return { success: false, id: 0, message: error.message };
    }
};

/**
 * Update a project skill requirement
 * @param {number} skillId - Skill ID (project_skills.id)
 * @param {Object} updateData - Update data (requiredLevel, isRequired, priority, sortOrder)
 * @returns {Promise<Object>} Result
 */
export const updateProjectSkill = async (skillId, updateData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/project-skill/${skillId}`, {
            method: 'PUT',
            credentials: 'include',
            body: JSON.stringify(updateData)
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error updating project skill:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a skill from a project
 * @param {number} skillId - Skill ID
 * @returns {Promise<Object>} Result
 */
export const deleteProjectSkill = async (skillId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/project-skill/${skillId}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error deleting project skill:', error);
        return { success: false, message: error.message };
    }
};

// =============================================
// Skill Endorsements Functions
// =============================================

/**
 * Get endorsements for a skill
 * @param {number} skillId - Skill ID (peoples_skills.id)
 * @returns {Promise<Array>} Array of endorsements
 */
export const getSkillEndorsements = async (skillId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/endorsements/${skillId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                skillId: item.peoples_skill_id ?? item.PEOPLES_SKILL_ID,
                endorsedBy: item.endorsed_by ?? item.ENDORSED_BY,
                endorserName: item.endorser_name ?? item.ENDORSER_NAME ?? '',
                endorserPhoto: item.endorser_photo ?? item.ENDORSER_PHOTO ?? '',
                comment: item.comment ?? item.COMMENT ?? '',
                createdAt: item.created_at ?? item.CREATED_AT
            }));
        }

        return [];
    } catch (error) {
        console.error('Error getting skill endorsements:', error);
        return [];
    }
};

/**
 * Add an endorsement to a skill
 * @param {number} skillId - Skill ID (peoples_skills.id)
 * @param {number} endorsedBy - Person ID who endorses
 * @param {string} comment - Optional comment
 * @returns {Promise<Object>} Result
 */
export const addSkillEndorsement = async (skillId, endorsedBy, comment = '') => {
    try {
        const response = await fetch(`${API_BASE_URL}/endorsements/${skillId}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ endorsedBy, comment })
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding skill endorsement:', error);
        return { success: false, id: 0, message: error.message };
    }
};

/**
 * Delete an endorsement
 * @param {number} endorsementId - Endorsement ID
 * @returns {Promise<Object>} Result
 */
export const deleteSkillEndorsement = async (endorsementId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/endorsements/${endorsementId}/remove`, {
            method: 'DELETE',
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error deleting skill endorsement:', error);
        return { success: false, message: error.message };
    }
};

// =============================================
// Search Functions
// =============================================

/**
 * Search people by skills
 * @param {string} skillUri - Skill URI to search for
 * @param {number} minProficiency - Minimum proficiency level (1-4)
 * @param {number} limit - Maximum results
 * @returns {Promise<Array>} Array of people matching the skill
 */
export const searchPeopleBySkills = async (skillUri, minProficiency = 1, limit = 50) => {
    try {
        const params = new URLSearchParams({
            skillUri,
            minProficiency: minProficiency.toString(),
            limit: limit.toString()
        });

        const response = await fetch(`${API_BASE_URL}/search/people?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                peopleId: item.people_id ?? item.PEOPLE_ID,
                peopleName: item.people_name ?? item.PEOPLE_NAME ?? '',
                peoplePhoto: item.people_photo ?? item.PEOPLE_PHOTO ?? '',
                proficiencyLevel: item.proficiency_level ?? item.PROFICIENCY_LEVEL ?? 2,
                yearsExperience: item.years_experience ?? item.YEARS_EXPERIENCE ?? 0,
                endorsementCount: item.endorsement_count ?? item.ENDORSEMENT_COUNT ?? 0
            }));
        }

        return [];
    } catch (error) {
        console.error('Error searching people by skills:', error);
        return [];
    }
};

/**
 * Find candidates matching a project's required skills
 * @param {number} projectId - Project ID
 * @param {number} limit - Maximum results
 * @returns {Promise<Array>} Array of candidates with skill match info
 */
export const findProjectCandidates = async (projectId, limit = 50) => {
    try {
        const params = new URLSearchParams({ limit: limit.toString() });

        const response = await fetch(`${API_BASE_URL}/search/project/${projectId}/candidates?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                peopleId: item.people_id ?? item.PEOPLE_ID,
                peopleName: item.people_name ?? item.PEOPLE_NAME ?? '',
                peoplePhoto: item.people_photo ?? item.PEOPLE_PHOTO ?? '',
                matchingSkills: item.matching_skills ?? item.MATCHING_SKILLS ?? 0,
                totalRequiredSkills: item.total_required_skills ?? item.TOTAL_REQUIRED_SKILLS ?? 0,
                avgProficiency: item.avg_proficiency ?? item.AVG_PROFICIENCY ?? 0,
                matchScore: item.match_score ?? item.MATCH_SCORE ?? 0
            }));
        }

        return [];
    } catch (error) {
        console.error('Error finding project candidates:', error);
        return [];
    }
};

/**
 * Get skills summary for a person
 * @param {number} peopleId - Person ID
 * @returns {Promise<Object>} Summary object
 */
export const getPeopleSkillsSummary = async (peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/summary/${peopleId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? {};

        if (success) {
            return {
                totalSkills: data.total_skills ?? data.TOTAL_SKILLS ?? 0,
                expertSkills: data.expert_skills ?? data.EXPERT_SKILLS ?? 0,
                advancedSkills: data.advanced_skills ?? data.ADVANCED_SKILLS ?? 0,
                intermediateSkills: data.intermediate_skills ?? data.INTERMEDIATE_SKILLS ?? 0,
                beginnerSkills: data.beginner_skills ?? data.BEGINNER_SKILLS ?? 0,
                highlightedSkills: data.highlighted_skills ?? data.HIGHLIGHTED_SKILLS ?? 0,
                totalEndorsements: data.total_endorsements ?? data.TOTAL_ENDORSEMENTS ?? 0
            };
        }

        return {
            totalSkills: 0,
            expertSkills: 0,
            advancedSkills: 0,
            intermediateSkills: 0,
            beginnerSkills: 0,
            highlightedSkills: 0,
            totalEndorsements: 0
        };
    } catch (error) {
        console.error('Error getting people skills summary:', error);
        return {
            totalSkills: 0,
            expertSkills: 0,
            advancedSkills: 0,
            intermediateSkills: 0,
            beginnerSkills: 0,
            highlightedSkills: 0,
            totalEndorsements: 0
        };
    }
};
